/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import {
	Navigate,
	Route,
	Routes,
	useNavigate,
	useParams,
} from 'react-router-dom';
import {
	AuthData,
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
} from '../../../context/auth';
import {
	PolicyContext,
	PolicyData,
	withPolicyContext,
} from '../../../context/policy';
import AuxDataView from '../../auxData/AuxDataView';
import { CatchUpInvoicePage } from '../../catchUps';
import CatchUpsPage from '../../catchUps/pages/CatchUpsPage';
import CreateCatchUpPage from '../../catchUps/pages/CreateCatchUpPage';
import { StyledContentPane } from '../../common';
import {
	CreateDisbursementPage,
	DisbursementPage,
	DisbursementsPage,
	EditDisbursementPage,
} from '../../disbursements';
import { useFastForm } from '../../fastForm';
import { getDefaultPolicyValues } from '../../fastForm/formBuilders';
import LeftSidebarLayout from '../../layouts/LeftSidebarLayout';
import PolicyLayout from '../../layouts/PolicyLayout';
import {
	CreatePaymentPage,
	EditPaymentPage,
	PaymentPage,
	PaymentsPage,
} from '../../payments';
import ProtectedRoute from '../../ProtectedRoute';
import CancellationsList from '../Cancellations/CancellationsList';
import ClaimsView from '../ClaimsView';
import DocumentsView from '../DocumentsView';
import EndorsementsList from '../Endorsements/EndorsementsList';
import Exposures from '../Exposures/Exposures';
import Fees from '../Fees/Fees';
import FutureInvoiceView from '../FutureInvoices/FutureInvoiceView';
import GracePeriods from '../GracePeriods';
import HistoricalQuotesView from '../HistoricalQuotesView';
import InvoicesView from '../InvoicesView';
import InvoiceView from '../InvoiceView';
import PolicyDetails from '../PolicyDetails';
import PolicyHistoryView from '../PolicyHistory/PolicyHistoryView';
import PolicyInvoiceLayout from '../PolicyInvoiceLayout';
import PolicyPaymentsLayout from '../PolicyPaymentsLayout';
import PolicyPricePreview from '../PolicyPricePreview';
import PremiumReportsView from '../PremiumReportsView';
import RenewalsList from '../Renewals/RenewalsList';
import PolicySidebar from '../Sidebar/PolicySidebar';
import ExposureRoutes from './ExposureRoutes';

interface Props {
	policyData: PolicyData;
	authData: AuthData;
}

// Acts as a MainLayout
// route: `/policy/:policyId`
const PolicyRoutes = (props: Props) => {
	const navigate = useNavigate();
	const { policyData, authData } = props;
	const { policyUpdateObj, setPolicyUpdateObj, policy, fields } = policyData;
	const params = useParams();
	const { policyId } = params;
	const defaultPolicyValues = getDefaultPolicyValues(fields, props.authData);
	const methods = useFastForm(fields, defaultPolicyValues);
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];

	if (!policy) {
		return null;
	}

	useEffect(() => {
		return () => {
			setPolicyUpdateObj({});
		};
	}, []);

	useEffect(() => {
		if (policyUpdateObj.updateFieldGroups) {
			const { fieldGroupsByLocator } =
				policy.characteristics[policy.characteristics.length - 1];

			// copy over current pending field group values to the policy's fieldGroupsByLocator.
			policyUpdateObj.updateFieldGroups.forEach((updateObj) => {
				const fieldGroupByLocator =
					fieldGroupsByLocator[updateObj.fieldGroupLocator];
				if (fieldGroupByLocator) {
					// update the current pending changes to fieldGroupsByLocator so changes are not deleted
					//
					fieldGroupsByLocator[updateObj.fieldGroupLocator] = {
						...updateObj.fieldValues,
					};
				}
			});
		}
	}, [policy]);

	const renderMain = () => {
		return (
			<StyledContentPane>
				<PolicyLayout>
					<Routes>
						<Route path='pricing' element={<PolicyPricePreview />} />
						<Route path='details' element={<PolicyDetails />} />
						<Route path='documents' element={<DocumentsView />} />
						<Route
							path='invoices/:invoiceId'
							element={
								<ProtectedRoute permission={'invoice.fetch'}>
									<InvoiceView />
								</ProtectedRoute>
							}
						/>
						<Route
							path='invoices'
							element={
								<ProtectedRoute permission={'invoice.fetch'}>
									<PolicyInvoiceLayout />
								</ProtectedRoute>
							}
						>
							<Route index element={<InvoicesView />} />
						</Route>
						<Route path='futureInvoices' element={<FutureInvoiceView />} />
						<Route path='historicalQuotes' element={<HistoricalQuotesView />} />
						<Route
							path='claims'
							element={
								<ProtectedRoute permission='claim.fetch'>
									<ClaimsView />
								</ProtectedRoute>
							}
						/>
						<Route
							path='premiumReports'
							element={
								<ProtectedRoute permission='premiumReport.fetch'>
									<PremiumReportsView />
								</ProtectedRoute>
							}
						/>
						<Route
							path='exposures/:exposureId/*'
							element={<ExposureRoutes />}
						/>
						<Route path='exposures' element={<Exposures />} />
						<Route
							path='cancellations'
							element={
								<ProtectedRoute permission='cancellation.fetch'>
									<CancellationsList />
								</ProtectedRoute>
							}
						/>
						<Route
							path='endorsements'
							element={
								<ProtectedRoute permission='endorsement.fetch'>
									<EndorsementsList />
								</ProtectedRoute>
							}
						/>
						<Route
							path='renewals'
							element={
								<ProtectedRoute permission='renewal.fetch'>
									<RenewalsList />
								</ProtectedRoute>
							}
						/>
						<Route path='fees' element={<Fees />} />
						<Route
							path='gracePeriods'
							element={
								<ProtectedRoute permission='gracePeriod.fetch'>
									<GracePeriods />
								</ProtectedRoute>
							}
						/>
						<Route path='policyHistory' element={<PolicyHistoryView />} />
						<Route
							path='auxData'
							element={
								<ProtectedRoute permission='auxData.fetch'>
									<AuxDataView
										navigate={navigate}
										entityLocator={policyId}
										entityType={'policy'}
									/>
								</ProtectedRoute>
							}
						/>
						{cashManagementEnabled && (
							<Route
								path='payments'
								element={
									<ProtectedRoute permission='payment.fetch'>
										<PolicyPaymentsLayout />
									</ProtectedRoute>
								}
							>
								<Route index element={<PaymentsPage />} />
								<Route path='new' element={<CreatePaymentPage />} />
								<Route path=':paymentLocator'>
									<Route index element={<PaymentPage />} />
									<Route path='edit' element={<EditPaymentPage />} />
								</Route>
							</Route>
						)}
						{cashManagementEnabled && (
							<Route
								path='disbursements'
								element={
									<ProtectedRoute permission='disbursement.fetch'>
										<PolicyPaymentsLayout />
									</ProtectedRoute>
								}
							>
								<Route index element={<DisbursementsPage />} />
								<Route path='new' element={<CreateDisbursementPage />} />
								<Route path=':disbursementLocator'>
									<Route index element={<DisbursementPage />} />
									<Route path='edit' element={<EditDisbursementPage />} />
								</Route>
							</Route>
						)}
						{cashManagementEnabled && (
							<Route
								path='catchUps'
								element={
									<ProtectedRoute permission={'catchUpInvoice.fetch'}>
										<PolicyPaymentsLayout />
									</ProtectedRoute>
								}
							>
								<Route index element={<CatchUpsPage />} />
								<Route path='new' element={<CreateCatchUpPage />} />
								<Route path=':catchUpLocator'>
									<Route index element={<CatchUpInvoicePage />} />
								</Route>
							</Route>
						)}
						<Route path={'*'} element={<Navigate replace to='details' />} />
					</Routes>
				</PolicyLayout>
			</StyledContentPane>
		);
	};

	const renderSidebar = () => {
		return <PolicySidebar />;
	};

	return (
		<FormProvider {...methods}>
			<PolicyContext.Provider value={policyData}>
				<LeftSidebarLayout
					leftSidebarContent={renderSidebar()}
					mainContent={renderMain()}
				/>
			</PolicyContext.Provider>
		</FormProvider>
	);
};

export default withPolicyContext<Props>(PolicyRoutes);
